import lodash from 'lodash'
import type { SyncProcedure } from '../../../app-core/lib/procedure/Procedure'
import type { Profile } from '../../../app-core/lib/user/Profile'
import { I18N_replaceSlug } from '../I18N/I18N.slugs'
import {
    IDB_doneReports,
    IDB_procedures,
    IDB_userProfiles,
    IDB_teams,
    IDB_posts,
    IDB_scheduledInstances,
} from '../IDB/IDB.dexie'
import type { Post_TPostId } from '../Post/Post.TPost'
import type { Rpts_ClientId } from '../Rpts/Rpts.types'
import type { Type_Workflow } from '../Type/Type.hive'
import { User_get } from '../User/User.get'
import type { DoneReport, ReportClientId } from '../../../shared/domain/reports/types.no-deps'
import type { UUID } from '../../../shared/lib/typeHelpers.no-deps'
import { WF_fetchByIds } from '../WF/WF.fetch'
import type { KnownUnit } from '../../../app-core/lib/unit/unit'
import { Unit_fetchByIds } from '../Unit/Unit.fetch'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { Logg_createLogger } from '../Logg/Logg.Logger'
import { compact, filter, uniq } from '../../../shared/lib/functional/array.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'
import { promiseThen } from '../../../shared/lib/functional/promiseThen.no-deps'
import { isNotNil } from '../../../shared/lib/functional/nil.no-deps'
import { getRecentForProcedureTraining } from '../Rpts/Rpts.getRecentForProcedureTraining'

// Todo.DoneReportDTO (Data Transfer Object)
export type Todo_DoneReportDTO = DoneReport

/** The ScheduledInstance identifier used for ad hoc reports */
const adHocReportId = 'ad-hoc'

// Todo.DoneReportDPO (Data Persistance Object)
export interface Todo_DoneReportDPO {
    report: number
    title?: string
    origin?: number
    ancestorReports?: ReportClientId[]
    isAction?: boolean
    clientId: Rpts_ClientId
    author: number
    scheduledInstance: UUID | typeof adHocReportId
    workflow: number
    procedure: number
    status: number
    commentCount: number
    startDate: number
    dueDate: number
    endDate: number
    isSyncing: boolean
    showOverdue: boolean
    description?: string
    teamDoneFor?: number
    actionsCreated?: number
    actionsCompleated?: number // Spelled wrong but used in IDB already, so can't change without compleating a migration
    subjectUnitId?: number
    isDeleted?: boolean
    isConfidential: boolean
}

export interface Todo_DoneReport {
    postId: Post_TPostId
    origin?: number
    ancestorReports?: ReportClientId[]
    isAction?: boolean
    title?: string
    scheduledInstance?: UUID | typeof adHocReportId
    clientId: Rpts_ClientId
    author: Profile
    workflow: Type_Workflow
    procedure: SyncProcedure
    commentCount: number
    startDate: Date
    isSyncing: boolean
    description?: string
    actionsCreated?: number
    actionsCompleted?: number
    teamDoneForName?: string
    unitSubject?: KnownUnit
    isConfidential?: boolean
}

export function Todo_toDoneReportDPO(doneReport: Todo_DoneReportDTO): Todo_DoneReportDPO {
    return {
        report: doneReport.report,
        title: doneReport.title,
        origin: doneReport.origin,
        ancestorReports: doneReport.ancestorReports as ReportClientId[],
        isAction: doneReport.isAction,
        clientId: doneReport.clientId as Rpts_ClientId,
        author: doneReport.author,
        workflow: doneReport.workflow,
        procedure: doneReport.procedure,
        status: doneReport.status,
        commentCount: doneReport.commentCount,
        startDate: +doneReport.startDate,
        endDate: +doneReport.endDate,
        scheduledInstance: doneReport.scheduledInstance != null ? doneReport.scheduledInstance : adHocReportId,
        dueDate: doneReport.dueDate != null ? +doneReport.dueDate : null,
        isSyncing: false,
        showOverdue: doneReport.showOverdue,
        description: doneReport.description,
        teamDoneFor: doneReport.teamDoneFor,
        actionsCreated: doneReport.actionsCreated,
        actionsCompleated: doneReport.actionsCompleted,
        subjectUnitId: doneReport.subjectUnitId,
        isDeleted: doneReport?.isDeleted,
        isConfidential: doneReport.isConfidential ?? false,
    }
}

export function Todo_isAction(todo: { isAction?: boolean }): boolean {
    return isNotNil(todo.isAction) && todo.isAction
}

/**
 * if report title has been set then report title will be displayed
 * else if workflow exists then workflow name will be displayed
 * else it will be an action and action description will be displayed
 */
export function Todo_doneReportName(doneReport: Todo_DoneReport): string {
    return doneReport.title ?? doneReport.workflow?.name ?? doneReport.description
}

/**
 * Todo.doneReportsPrune deletes old done reports so that they don't show up
 * on the done tab on the home Todo screen
 */
export async function Todo_doneReportsPrune(endDateThreshold: Date, keepRecentTrainingReports = true) {
    const logger = Logg_createLogger(`Todo_doneReportsPrune`)
    try {
        // Delete ad hoc reports after the first 50 excluding any that are the latest for procedure training
        let completedReportsToKeep: ReportClientId[] = []
        if (keepRecentTrainingReports) {
            const recentTrainingReports = await getRecentForProcedureTraining()
            completedReportsToKeep = recentTrainingReports.flatMap((data) =>
                data.training.map((t) => t.report.clientId)
            )
        }

        const adHocReportsToDelete = (
            await IDB_doneReports()
                .where(['scheduledInstance', 'startDate'])
                .between([adHocReportId, 0], [adHocReportId, Infinity])
                .reverse()
                .offset(50)
                .primaryKeys()
        ).filter((clientId) => !completedReportsToKeep.includes(clientId))

        let count = await IDB_doneReports().where('clientId').anyOf(adHocReportsToDelete).delete()

        if (count > 0) {
            logger.log(`deleted ad hoc done reports`, { count })
        }

        const scheduledInstances = await IDB_scheduledInstances().toArray()
        const scheduledInstanceIds = new Set(scheduledInstances.map((SI) => SI.id))
        const isNotAdHocAndDoesNotHaveScheduledInstances = (report: Todo_DoneReportDPO) =>
            report.scheduledInstance !== adHocReportId && !scheduledInstanceIds.has(report.scheduledInstance)

        const isNotLatest = (report: Todo_DoneReportDPO) => !completedReportsToKeep.includes(report.clientId)

        // Prune done reports if the corresponding schedule instance(s) have already been pruned
        // and the 'done date' is older than the provided threshold
        // and is not the latest report for the procedure training
        count = await IDB_doneReports()
            .filter((r) => isNotLatest(r) && isNotAdHocAndDoesNotHaveScheduledInstances(r))
            .filter(({ endDate }) => endDate <= endDateThreshold.valueOf())
            .delete()

        if (count > 0) {
            logger.log(`deleted scheduled done reports`, { count })
        }
    } catch (error) {
        logger.error(error)
    }
}

interface CreateArgs {
    reportClientId: Rpts_ClientId
    title?: string
    origin?: number
    isAction: boolean
    workflow: { id: number }
    procedure: { id: number }
    startDate: Date
    endDate: Date
    scheduledInstance?: {
        id: UUID
        dueDate: Date
        showOverdue: boolean
        description: string
    }
    teamDoneFor?: number
    subjectUnitId?: number
}

/**
 * Todo.doneReportCreate creates a done report after the report been finalized
 */
export async function Todo_doneReportCreate(logger: ClientLogger, args: CreateArgs) {
    logger.log(`saving done report`, { reportClientId: args.reportClientId })
    await IDB_doneReports().put({
        report: -1,
        title: args.title,
        origin: args.origin,
        isAction: args.isAction,
        clientId: args.reportClientId,
        author: User_get().id,
        workflow: args.workflow ? args.workflow.id : -1,
        procedure: args.procedure ? args.procedure.id : -1,
        status: null,
        commentCount: 0,
        startDate: +args.startDate,
        endDate: +args.endDate,
        isSyncing: true,
        scheduledInstance: args.scheduledInstance ? args.scheduledInstance.id : null,
        dueDate: args.scheduledInstance ? +args.scheduledInstance.dueDate : null,
        showOverdue: args.scheduledInstance ? args.scheduledInstance.showOverdue : null,
        description: args.scheduledInstance ? args.scheduledInstance.description : null,
        teamDoneFor: args.teamDoneFor,
        subjectUnitId: args.subjectUnitId,
        isConfidential: false,
    })
    logger.log(`saved done report`, { reportClientId: args.reportClientId })
}

/**
 * Todo.doneReportsFetch reads the done reports from idb and any other
 * related entities need in order to display them on the done tab
 */
export async function Todo_doneReportsFetch(logger: ClientLogger, forUser: number): Promise<Todo_DoneReport[]> {
    const doneReports = (await IDB_doneReports().orderBy('startDate').reverse().toArray()).filter(
        (report) => !report?.isDeleted
    )
    const [authors, workflows, procedures, units, posts, teams] = await Promise.all([
        pipe(
            doneReports.map((r) => r.author),
            uniq,
            (ids) => IDB_userProfiles().where('userId').anyOf(ids).toArray(),
            promiseThen(compact)
        ),
        pipe(
            doneReports.map((r) => r.workflow),
            compact,
            filter((w) => w !== -1),
            uniq,
            (ids) => WF_fetchByIds(logger, ids),
            promiseThen(compact)
        ),
        pipe(
            doneReports.map((r) => r.procedure),
            compact,
            filter((p) => p !== -1),
            uniq,
            (ids) => IDB_procedures().where('id').anyOf(ids).toArray(),
            promiseThen(compact)
        ),
        pipe(
            doneReports.map((r) => r.subjectUnitId),
            compact,
            uniq,
            Unit_fetchByIds
        ),
        pipe(
            doneReports.map((r) => r.clientId),
            (ids) =>
                IDB_posts()
                    .where('reportClientId')
                    .anyOf(ids)
                    .filter((p) => p.contentType === 'report')
                    .toArray()
        ),
        pipe(
            doneReports.map((r) => r.teamDoneFor),
            compact,
            uniq,
            (ids) => IDB_teams().where('id').anyOf(ids).toArray(),
            promiseThen(compact)
        ),
    ])

    return lodash.compact(
        doneReports
            .filter((doneReport) => doneReport.author === forUser)
            .map((doneReport) => {
                try {
                    const author = authors.find((u) => u.userId === doneReport.author) ?? null
                    const workflow = workflows.find((w) => w.id === doneReport.workflow) ?? null
                    const procedure = procedures.find((p) => p.id === doneReport.procedure) ?? null
                    const unitSubject = units.find((u) => u.id === doneReport.subjectUnitId) ?? null
                    const teamDoneFor = teams.find((t) => t.id === doneReport.teamDoneFor) ?? null
                    const post =
                        posts.find((p) => p.contentType === 'report' && p.reportClientId === doneReport.clientId) ??
                        null

                    if (author == null) {
                        logger.error('Could not find author for done report', { userId: doneReport.author })
                        return null
                    }

                    const teamDoneForName = teamDoneFor?.name

                    return {
                        postId: post && post.id,
                        title: doneReport.title,
                        origin: doneReport.origin,
                        isAction: doneReport.isAction,
                        scheduledInstance: doneReport.scheduledInstance,
                        clientId: doneReport.clientId,
                        author,
                        workflow: workflow
                            ? {
                                  ...workflow,
                                  name: I18N_replaceSlug(workflow.name),
                              }
                            : null,
                        procedure: procedure
                            ? {
                                  ...procedure,
                                  name: I18N_replaceSlug(procedure.name),
                              }
                            : null,
                        commentCount: doneReport.commentCount,
                        startDate: new Date(doneReport.startDate),
                        isSyncing: doneReport.isSyncing,
                        description: doneReport.description,
                        actionsCreated: doneReport.actionsCreated,
                        actionsCompleted: doneReport.actionsCompleated,
                        teamDoneForName,
                        unitSubject,
                        isConfidential: doneReport.isConfidential,
                    }
                } catch (error) {
                    logger.error(error, { reportClientId: doneReport.clientId })
                    return null
                }
            })
    )
}
